import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getDevice,
  subscribeToDevice,
  updateDeviceHeartbeat,
  deviceLogout
} from '../services/devices';
import { getCompany } from '../services/companies';
import { getTimer, subscribeToTimer } from '../services/vpsApi';
import { getSlideshowsForDevice, subscribeToSlideshows } from '../services/slideshows';
import { getActiveAnimationForDevice } from '../services/animations';
import TimerEmbed from './TimerEmbed';
import './DeviceDisplay.css';

function DeviceDisplay() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [company, setCompany] = useState(null);
  const [timer, setTimer] = useState(null);
  const [slideshows, setSlideshows] = useState([]);
  const [animation, setAnimation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showKickedOutMessage, setShowKickedOutMessage] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const sessionId = localStorage.getItem('sessionId');
  const heartbeatInterval = useRef(null);
  const timerUnsubscribe = useRef(null);
  const slideshowsUnsubscribe = useRef(null);

  // Load device data
  useEffect(() => {
    if (!deviceId || !sessionId) {
      navigate('/company-login');
      return;
    }

    const loadDevice = async () => {
      try {
        const deviceData = await getDevice(deviceId);
        setDevice(deviceData);

        // Load company data
        if (deviceData.companyId) {
          const companyData = await getCompany(deviceData.companyId);
          setCompany(companyData);
        }

        setLoading(false);

        // Check if this session is still valid
        if (deviceData.sessionId !== sessionId) {
          setShowKickedOutMessage(true);
          setTimeout(() => {
            handleLogout();
          }, 3000);
        }
      } catch (err) {
        console.error('Error loading device:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadDevice();
  }, [deviceId, sessionId, navigate]);

  // Subscribe to device changes
  useEffect(() => {
    if (!deviceId) return;

    const unsubscribe = subscribeToDevice(deviceId, (deviceData) => {
      if (!deviceData) {
        alert('設備已被刪除');
        handleLogout();
        return;
      }

      setDevice(deviceData);

      // Check if session has been kicked out
      if (deviceData.sessionId !== sessionId) {
        setShowKickedOutMessage(true);
        setTimeout(() => {
          handleLogout();
        }, 3000);
      }
    });

    return () => unsubscribe();
  }, [deviceId, sessionId]);

  // Heartbeat to keep session alive
  useEffect(() => {
    if (!deviceId) return;

    heartbeatInterval.current = setInterval(async () => {
      try {
        await updateDeviceHeartbeat(deviceId);
      } catch (err) {
        console.error('Heartbeat error:', err);
      }
    }, 30000); // Every 30 seconds

    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, [deviceId]);

  // Subscribe to timer if device has timerId
  useEffect(() => {
    if (!device?.timerId) {
      setTimer(null);
      if (timerUnsubscribe.current) {
        timerUnsubscribe.current();
        timerUnsubscribe.current = null;
      }
      return;
    }

    const loadTimer = async () => {
      try {
        const timerData = await getTimer(device.timerId);
        setTimer(timerData);
      } catch (err) {
        console.error('Error loading timer:', err);
      }
    };

    loadTimer();

    // Subscribe to timer updates
    timerUnsubscribe.current = subscribeToTimer(device.timerId, (timerData) => {
      setTimer(timerData);
    });

    return () => {
      if (timerUnsubscribe.current) {
        timerUnsubscribe.current();
        timerUnsubscribe.current = null;
      }
    };
  }, [device?.timerId]);

  // Load slideshows
  useEffect(() => {
    if (!device?.companyId || !company) return;

    const loadSlideshows = async () => {
      try {
        const data = await getSlideshowsForDevice(
          device.companyId,
          company.settings?.allowGlobalAds || true
        );
        setSlideshows(data);
      } catch (err) {
        console.error('Error loading slideshows:', err);
      }
    };

    loadSlideshows();

    // Subscribe to slideshow updates
    slideshowsUnsubscribe.current = subscribeToSlideshows(
      device.companyId,
      company.settings?.allowGlobalAds || true,
      (data) => {
        setSlideshows(data);
      }
    );

    return () => {
      if (slideshowsUnsubscribe.current) {
        slideshowsUnsubscribe.current();
        slideshowsUnsubscribe.current = null;
      }
    };
  }, [device?.companyId, company]);

  // Load active animation
  useEffect(() => {
    if (!device?.id || !device?.companyId) return;

    const loadAnimation = async () => {
      try {
        const animData = await getActiveAnimationForDevice(device.id, device.companyId);
        setAnimation(animData);
      } catch (err) {
        console.error('Error loading animation:', err);
      }
    };

    loadAnimation();

    // Reload animation every 30 seconds
    const interval = setInterval(loadAnimation, 30000);

    return () => clearInterval(interval);
  }, [device?.id, device?.companyId]);

  // Slideshow rotation
  useEffect(() => {
    if (!slideshows || slideshows.length === 0) return;
    if (device?.timerId) return; // Don't rotate if timer is active

    const currentSlideshow = slideshows[currentSlide];
    const duration = (currentSlideshow?.duration || 10) * 1000;

    const timeout = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshows.length);
    }, duration);

    return () => clearTimeout(timeout);
  }, [slideshows, currentSlide, device?.timerId]);

  const handleLogout = async () => {
    try {
      await deviceLogout(deviceId);
      localStorage.removeItem('deviceId');
      localStorage.removeItem('deviceCode');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('companyId');
      localStorage.removeItem('companyCode');
      localStorage.removeItem('companyName');
      navigate('/company-login');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/company-login');
    }
  };

  const handleManualRefresh = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="device-display-loading">
        <div className="loading-spinner"></div>
        <p>載入中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="device-display-error">
        <h2>載入失敗</h2>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={() => navigate('/company-login')}>
          返回登入
        </button>
      </div>
    );
  }

  if (showKickedOutMessage) {
    return (
      <div className="device-kicked-out">
        <div className="kicked-out-message">
          <h2>⚠️ 會話已結束</h2>
          <p>此設備已在其他地方登入</p>
          <p>即將返回登入頁面...</p>
        </div>
      </div>
    );
  }

  // Priority 1: Animation (background)
  const backgroundAnimation = animation?.animationUrl || company?.settings?.defaultAnimationUrl;

  // Priority 2: Timer (if timerId is set)
  if (device?.timerId && timer) {
    return (
      <div className="device-display">
        {backgroundAnimation && (
          <div className="background-animation">
            <video
              src={backgroundAnimation}
              autoPlay
              loop
              muted
              playsInline
              className="animation-video"
            />
          </div>
        )}

        <div className="timer-content">
          <TimerEmbed timer={timer} embedded={true} />
        </div>

        <div className={`device-menu ${showMenu ? 'show' : ''}`}>
          <button
            className="menu-toggle"
            onClick={() => setShowMenu(!showMenu)}
          >
            ⚙️
          </button>
          {showMenu && (
            <div className="menu-options">
              <button onClick={handleManualRefresh}>
                🔄 手動更新
              </button>
              <button onClick={handleLogout}>
                🚪 退出
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Priority 3: Slideshows (if no timer)
  if (slideshows && slideshows.length > 0) {
    return (
      <div className="device-display">
        {backgroundAnimation && (
          <div className="background-animation">
            <video
              src={backgroundAnimation}
              autoPlay
              loop
              muted
              playsInline
              className="animation-video"
            />
          </div>
        )}

        <div className="slideshow-container">
          {slideshows.map((slideshow, index) => {
            const isVideo = slideshow.mediaType === 'video';

            return (
              <div
                key={slideshow.id}
                className={`slide ${index === currentSlide ? 'active' : ''}`}
              >
                {isVideo ? (
                  <video
                    src={slideshow.mediaUrl}
                    autoPlay={index === currentSlide}
                    loop
                    muted
                    playsInline
                    className="slide-video"
                  />
                ) : (
                  <img
                    src={slideshow.mediaUrl}
                    alt={slideshow.title}
                    className="slide-image"
                  />
                )}
              </div>
            );
          })}

          {slideshows.length > 1 && (
            <div className="slideshow-indicators">
              {slideshows.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
          )}
        </div>

        <div className={`device-menu ${showMenu ? 'show' : ''}`}>
          <button
            className="menu-toggle"
            onClick={() => setShowMenu(!showMenu)}
          >
            ⚙️
          </button>
          {showMenu && (
            <div className="menu-options">
              <button onClick={handleManualRefresh}>
                🔄 手動更新
              </button>
              <button onClick={handleLogout}>
                🚪 退出
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Priority 4: No content
  return (
    <div className="device-display">
      {backgroundAnimation && (
        <div className="background-animation">
          <video
            src={backgroundAnimation}
            autoPlay
            loop
            muted
            playsInline
            className="animation-video"
          />
        </div>
      )}

      <div className="device-no-content">
        <h2>{device.deviceName || '顯示器設備'}</h2>
        <p>尚未設定內容</p>
        <p className="help-text">請聯繫管理員設定計時器或輪播內容</p>
      </div>

      <div className={`device-menu ${showMenu ? 'show' : ''}`}>
        <button
          className="menu-toggle"
          onClick={() => setShowMenu(!showMenu)}
        >
          ⚙️
        </button>
        {showMenu && (
          <div className="menu-options">
            <button onClick={handleManualRefresh}>
              🔄 手動更新
            </button>
            <button onClick={handleLogout}>
              🚪 退出
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceDisplay;
